// src/components/AdminPortal/HeroSection.js
import React, { useState, useRef, useEffect } from "react";
import {
  FiEdit,
  FiPlusCircle,
  FiTrash2,
  FiX,
  FiMoreVertical,
  FiChevronDown,
  FiChevronRight,
} from "react-icons/fi"; // Re-added FiChevron icons
import "../ContentManagement.css";

const HeroSection = () => {
  const [heroSections, setHeroSections] = useState([
    {
      id: 1,
      title: "Welcome to Our Website!",
      subtitle: "We provide excellent services.",
      description:
        "This is a brief description of what we offer on our homepage. It sets the tone for visitors.",
      type: "Image",
      sectionContentLink:
        "https://via.placeholder.com/800x300?text=Default+Hero+Image+1",
      sectionBlob: null,
      displayOrder: 1,
      isExpanded: false, // Added isExpanded for expand/collapse
    },
    {
      id: 2,
      title: "Discover Our Innovations",
      subtitle: "Leading the Future Today",
      description:
        "Explore our latest breakthroughs and how they are shaping the industry.",
      type: "Video",
      sectionContentLink: "https://www.w3schools.com/html/movie.mp4",
      sectionBlob: null,
      displayOrder: 2,
      isExpanded: false, // Added isExpanded for expand/collapse
    },
  ]);

  const [editingHeroId, setEditingHeroId] = useState(null);
  const [tempHeroContent, setTempHeroContent] = useState(null);

  const [showAddHeroModal, setShowAddHeroModal] = useState(false);
  const [newHeroContent, setNewHeroContent] = useState({
    title: "",
    subtitle: "",
    description: "",
    type: "Text",
    sectionContentLink: "",
    sectionBlob: null,
    displayOrder:
      heroSections.length > 0
        ? Math.max(...heroSections.map((s) => s.displayOrder)) + 1
        : 1,
  });

  // State for controlling which card's kebab menu dropdown is open
  const [openMenuHeroId, setOpenMenuHeroId] = useState(null);
  // Ref to detect clicks outside the dropdown menu
  const menuRef = useRef(null);

  // Effect to close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (menuRef.current && !menuRef.current.contains(event.target)) {
        setOpenMenuHeroId(null);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, [openMenuHeroId]);

  // Function to toggle kebab menu visibility for a hero card
  const handleToggleMenu = (id, event) => {
    event.stopPropagation(); // Prevent click from bubbling up (e.g., closing another menu)
    setOpenMenuHeroId(openMenuHeroId === id ? null : id);
  };

  // Function to toggle expand/collapse for card details
  const handleToggleExpand = (id) => {
    setHeroSections((prev) =>
      prev.map((hero) =>
        hero.id === id ? { ...hero, isExpanded: !hero.isExpanded } : hero
      )
    );
  };

  const handleAddClick = () => {
    setShowAddHeroModal(true);
    setNewHeroContent({
      title: "",
      subtitle: "",
      description: "",
      type: "Text",
      sectionContentLink: "",
      sectionBlob: null,
      displayOrder:
        heroSections.length > 0
          ? Math.max(...heroSections.map((s) => s.displayOrder)) + 1
          : 1,
    });
  };

  const handleAddModalClose = () => {
    setShowAddHeroModal(false);
  };

  const handleNewHeroChange = (e) => {
    const { name, value } = e.target;
    setNewHeroContent((prev) => ({ ...prev, [name]: value }));
  };

  const handleNewHeroFileChange = (e, contentType) => {
    if (e.target.files && e.target.files[0]) {
      const file = e.target.files[0];
      const reader = new FileReader();
      reader.onload = (event) => {
        setNewHeroContent((prev) => ({
          ...prev,
          sectionContentLink: event.target.result,
          sectionBlob: file,
          type: contentType,
        }));
      };
      reader.readAsDataURL(file);
    } else {
      setNewHeroContent((prev) => ({
        ...prev,
        sectionContentLink: "",
        sectionBlob: null,
      }));
    }
  };

  const handleSaveNewHero = (e) => {
    e.preventDefault();

    const newId = Math.max(...heroSections.map((s) => s.id), 0) + 1;
    const finalNewHero = { ...newHeroContent, id: newId, isExpanded: false }; // New items start collapsed

    setHeroSections((prev) => [...prev, finalNewHero]);
    handleAddModalClose();
    console.log("New Hero Section Saved:", finalNewHero);
  };

  const handleEditClick = (hero) => {
    setEditingHeroId(hero.id);
    setTempHeroContent({ ...hero });
    setOpenMenuHeroId(null); // Close the dropdown menu after clicking edit
  };

  const handleDeleteClick = (id) => {
    if (
      window.confirm(
        "Are you sure you want to delete this Hero Section content? This cannot be undone."
      )
    ) {
      setHeroSections((prev) => prev.filter((hero) => hero.id !== id));
      if (editingHeroId === id) {
        setEditingHeroId(null);
        setTempHeroContent(null);
      }
      setOpenMenuHeroId(null); // Close the dropdown menu after clicking delete
    }
  };

  const handleSaveClick = () => {
    setHeroSections((prev) =>
      prev.map((hero) => (hero.id === editingHeroId ? tempHeroContent : hero))
    );
    setEditingHeroId(null);
    setTempHeroContent(null);
  };

  const handleCancelClick = () => {
    setEditingHeroId(null);
    setTempHeroContent(null);
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setTempHeroContent((prev) => ({ ...prev, [name]: value }));
  };

  const handleFileChange = (e, contentType) => {
    if (e.target.files && e.target.files[0]) {
      const file = e.target.files[0];
      const reader = new FileReader();
      reader.onload = (event) => {
        setTempHeroContent((prev) => ({
          ...prev,
          sectionContentLink: event.target.result,
          sectionBlob: file,
          type: contentType,
        }));
      };
      reader.readAsDataURL(file);
    } else {
      setTempHeroContent((prev) => ({
        ...prev,
        sectionContentLink: "",
        sectionBlob: null,
      }));
    }
  };

  return (
    <div className="content-management-container">
      <div className="section-header-row">
        <h2>Hero Section Management</h2>
        <button onClick={handleAddClick} className="add-new-btn">
          <FiPlusCircle size={18} />
        </button>
      </div>

      {heroSections.length === 0 && !editingHeroId && !showAddHeroModal && (
        <p>
          No Hero Sections added yet. Click "Add New Hero Section" to create
          one.
        </p>
      )}

      {heroSections
        .sort((a, b) => a.displayOrder - b.displayOrder)
        .map((hero) => (
          <div key={hero.id} className="content-card">
            {editingHeroId === hero.id ? (
              // Edit Form for an existing item
              <div className="edit-form">
                <label>
                  Title:
                  <input
                    type="text"
                    name="title"
                    value={tempHeroContent.title}
                    onChange={handleChange}
                  />
                </label>
                <label>
                  Subtitle:
                  <input
                    type="text"
                    name="subtitle"
                    value={tempHeroContent.subtitle}
                    onChange={handleChange}
                  />
                </label>
                <label>
                  Description:
                  <textarea
                    name="description"
                    value={tempHeroContent.description}
                    onChange={handleChange}
                  ></textarea>
                </label>
                <label>
                  Content Type:
                  <select
                    name="type"
                    value={tempHeroContent.type}
                    onChange={handleChange}
                  >
                    <option value="Text">Text</option>
                    <option value="Image">Image</option>
                    <option value="Video">Video</option>
                  </select>
                </label>

                {(tempHeroContent.type === "Image" ||
                  tempHeroContent.type === "Video") && (
                  <label>
                    {tempHeroContent.type} File:
                    <input
                      type="file"
                      accept={
                        tempHeroContent.type === "Image" ? "image/*" : "video/*"
                      }
                      onChange={(e) =>
                        handleFileChange(e, tempHeroContent.type)
                      }
                    />
                  </label>
                )}
                {tempHeroContent.sectionContentLink &&
                  tempHeroContent.type === "Image" && (
                    <img
                      src={tempHeroContent.sectionContentLink}
                      alt="Hero Preview"
                      className="content-image-preview"
                    />
                  )}
                {tempHeroContent.sectionContentLink &&
                  tempHeroContent.type === "Video" && (
                    <video
                      src={tempHeroContent.sectionContentLink}
                      controls
                      className="content-video-preview"
                    />
                  )}
                {(tempHeroContent.type === "Text" ||
                  tempHeroContent.type === "Video") && (
                  <label>
                    {tempHeroContent.type === "Text"
                      ? "Content Link (Optional):"
                      : "Video URL (for external video):"}
                    <input
                      type="text"
                      name="sectionContentLink"
                      value={tempHeroContent.sectionContentLink || ""}
                      onChange={handleChange}
                    />
                  </label>
                )}

                <label>
                  Display Order:
                  <input
                    type="number"
                    name="displayOrder"
                    value={tempHeroContent.displayOrder}
                    onChange={handleChange}
                  />
                </label>

                <div className="form-actions">
                  <button onClick={handleSaveClick} className="btn-save">
                    Save
                  </button>
                  <button onClick={handleCancelClick} className="btn-cancel">
                    Cancel
                  </button>
                </div>
              </div>
            ) : (
              // Display View with Expand/Collapse and Kebab Menu
              <>
                <div
                  className="content-card-header"
                  onClick={() => handleToggleExpand(hero.id)}
                >
                  <div className="card-header-main">
                    <h3>{hero.title}</h3>
                    <span className="expand-toggle">
                      {hero.isExpanded ? (
                        <FiChevronDown size={24} />
                      ) : (
                        <FiChevronRight size={24} />
                      )}
                    </span>
                  </div>
                  <div
                    className="menu-dropdown-container"
                    ref={openMenuHeroId === hero.id ? menuRef : null}
                  >
                    <button
                      className="menu-dropdown-button"
                      onClick={(e) => handleToggleMenu(hero.id, e)}
                    >
                      <FiMoreVertical size={24} />
                    </button>
                    {openMenuHeroId === hero.id && (
                      <div className="menu-dropdown-content">
                        <button
                          onClick={() => handleEditClick(hero)}
                          className="dropdown-item"
                        >
                          <FiEdit size={16} /> Edit
                        </button>
                        <button
                          onClick={() => handleDeleteClick(hero.id)}
                          className="dropdown-item delete-item"
                        >
                          <FiTrash2 size={16} /> Delete
                        </button>
                      </div>
                    )}
                  </div>
                </div>

                {hero.isExpanded && (
                  <div className="content-details-expanded">
                    <p>
                      <strong>Subtitle:</strong> {hero.subtitle}
                    </p>
                    <p>
                      <strong>Description:</strong> {hero.description}
                    </p>
                    <p>
                      <strong>Type:</strong> {hero.type}
                    </p>
                    <p>
                      <strong>Display Order:</strong> {hero.displayOrder}
                    </p>

                    {hero.type === "Image" && hero.sectionContentLink && (
                      <img
                        src={hero.sectionContentLink}
                        alt="Hero"
                        className="content-image"
                      />
                    )}
                    {hero.type === "Video" && hero.sectionContentLink && (
                      <video
                        src={hero.sectionContentLink}
                        controls
                        className="content-video"
                      />
                    )}
                    {hero.type === "Text" && hero.sectionContentLink && (
                      <p>
                        <strong>Link:</strong>{" "}
                        <a
                          href={hero.sectionContentLink}
                          target="_blank"
                          rel="noopener noreferrer"
                        >
                          {hero.sectionContentLink}
                        </a>
                      </p>
                    )}
                  </div>
                )}
              </>
            )}
          </div>
        ))}

      {/* Add Hero Section Modal (unchanged from previous step) */}
      {showAddHeroModal && (
        <div className="modal-overlay">
          <div className="modal-content">
            <div className="modal-header">
              <h3>Add New Hero Section</h3>
              <button className="modal-close-btn" onClick={handleAddModalClose}>
                <FiX size={24} />
              </button>
            </div>
            <form onSubmit={handleSaveNewHero} className="edit-form">
              <label>
                Display Order:
                <input
                  type="number"
                  name="displayOrder"
                  value={newHeroContent.displayOrder}
                  onChange={handleNewHeroChange}
                />
              </label>
              <label>
                Title:
                <input
                  type="text"
                  name="title"
                  value={newHeroContent.title}
                  onChange={handleNewHeroChange}
                  required
                />
              </label>
              <label>
                Subtitle:
                <input
                  type="text"
                  name="subtitle"
                  value={newHeroContent.subtitle}
                  onChange={handleNewHeroChange}
                />
              </label>
              <label>
                Description:
                <textarea
                  name="description"
                  value={newHeroContent.description}
                  onChange={handleNewHeroChange}
                  required
                ></textarea>
              </label>
              <label>
                Title:
                <input
                  type="text"
                  name="title"
                  value={newHeroContent.title}
                  onChange={handleNewHeroChange}
                  required
                />
              </label>
              <label>
                Content:
                <input
                  type="text"
                  name="subtitle"
                  value={newHeroContent.subtitle}
                  onChange={handleNewHeroChange}
                />
              </label>

              <label>
                Content Type:
                <select
                  name="type"
                  value={newHeroContent.type}
                  onChange={handleNewHeroChange}
                >
                  <option value="Text"></option>
                  <option value="Image">Image</option>
                  <option value="Video">Video</option>
                </select>
              </label>

              {(newHeroContent.type === "Image" ||
                newHeroContent.type === "Video") && (
                <label>
                  {newHeroContent.type} File:
                  <input
                    type="file"
                    accept={
                      newHeroContent.type === "Image" ? "image/*" : "video/*"
                    }
                    onChange={(e) =>
                      handleNewHeroFileChange(e, newHeroContent.type)
                    }
                  />
                </label>
              )}
              {newHeroContent.sectionContentLink &&
                newHeroContent.type === "Image" && (
                  <img
                    src={newHeroContent.sectionContentLink}
                    alt="Hero Preview"
                    className="content-image-preview"
                  />
                )}
              {newHeroContent.sectionContentLink &&
                newHeroContent.type === "Video" && (
                  <video
                    src={newHeroContent.sectionContentLink}
                    controls
                    className="content-video-preview"
                  />
                )}
              {/* {(newHeroContent.type === 'Text' || newHeroContent.type === 'Video') && (
                                <label>
                                    {newHeroContent.type === 'Text' ? 'Content Link (Optional):' : 'Video URL (for external video):'}
                                    <input type="text" name="sectionContentLink" value={newHeroContent.sectionContentLink || ''} onChange={handleNewHeroChange} />
                                </label>
                            )} */}

              <div className="form-actions">
                <button
                  type="button"
                  className="btn-cancel"
                  onClick={handleAddModalClose}
                >
                  Cancel
                </button>
                <button type="submit" className="btn-save">
                  Save
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default HeroSection;
